import {
    getFirestore, collection, query, orderBy, limit, getDocs,
    where, Timestamp, doc, setDoc, getDoc, serverTimestamp, deleteDoc
} from "firebase/firestore";
import { app } from "../firebase-config.js";

const db = getFirestore(app);

export const AdminEconomyService = {
    /**
     * Better Whale Watch: Get Top Users by Activity Points
     * Uses efficient index-based ordering.
     */
    getWhales: async (limitCount = 10) => {
        try {
            const q = query(
                collection(db, "users"),
                orderBy("activityPoints", "desc"),
                limit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error("AdminEconomyService.getWhales Error:", error);
            // Fallback for missing index: Return empty or handle gracefully
            return [];
        }
    },

    /**
     * Daily Economy Flow: Earned vs Used (Today)
     * Uses Aggregation Queries (Cost effective: 1 read per aggregation)
     */
    getDailyFlow: async () => {
        try {
            const today = new Date();
            today.setHours(0, 0, 0, 0);
            const todayTimestamp = Timestamp.fromDate(today);

            const coll = collection(db, "point_transactions");

            // Stability Fix: Use Client-side Aggregation
            // Avoids complex index issues and 'failed-precondition' errors.
            // Cost: N reads (acceptable for daily volume < 5000)
            const q = query(
                coll,
                where("createdAt", ">=", todayTimestamp),
                limit(5000) // Safety Limit
            );

            const snapshot = await getDocs(q);

            let minted = 0;
            let burned = 0;
            let revenue = 0;
            let totalBonusRate = 0;
            let chargeCount = 0;

            snapshot.forEach(doc => {
                const data = doc.data();
                if (data.type === 'earn') {
                    minted += (data.amount || 0);

                    // Revenue & Bonus (Source: 'charge')
                    if (data.source === 'charge' || data.source === 'payment') {
                        // 1. Revenue
                        const payAmt = data.metadata?.paymentAmount || (data.amount || 0); // Fallback: 1:1 if missing
                        revenue += payAmt;

                        // 2. Bonus Rate
                        const rate = data.metadata?.bonusRate || 0;
                        totalBonusRate += rate;
                        chargeCount++;
                    }
                }
                if (data.type === 'use') burned += (data.amount || 0);
            });

            const avgBonus = chargeCount > 0 ? (totalBonusRate / chargeCount).toFixed(1) : 0;

            return { minted, burned: Math.abs(burned), revenue, avgBonus };

        } catch (error) {
            console.error("AdminEconomyService.getDailyFlow Error:", error);
            return { minted: 0, burned: 0 };
        }
    },

    /**
     * Recent Transaction Stream (Live Ticker)
     */
    getRecentTransactions: async (limitCount = 20) => {
        try {
            const q = query(
                collection(db, "point_transactions"),
                orderBy("createdAt", "desc"),
                limit(limitCount)
            );
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
        } catch (error) {
            console.error("AdminEconomyService.getRecentTransactions Error:", error);
            return [];
        }
    },

    /**
     * [SERVER] Get All Economy Stats (Secure & Fast)
     * Replaces getDailyFlow, getWhales, getRecentTransactions
     */
    getEconomyStats_Server: async () => {
        try {
            const { getFunctions, httpsCallable } = await import("firebase/functions");
            const functions = getFunctions(app);
            const getStats = httpsCallable(functions, 'getEconomyStats');

            const result = await getStats();
            return result.data; // { stats, whales, transactions }
        } catch (error) {
            console.error("AdminEconomyService.getEconomyStats_Server Error:", error);
            throw error;
        }
    },
    /**
     * [SWIS] Get Global Economy Settings
     */
    getGlobalEconomy: async () => {
        try {
            const docRef = doc(db, "settings", "global_economy");
            const snapshot = await getDoc(docRef);
            if (snapshot.exists()) return snapshot.data();
            return null; // Return null if not initialized
        } catch (error) {
            console.error("AdminEconomyService.getGlobalEconomy Error:", error);
            throw error;
        }
    },

    /**
     * [SWIS] Update Global Economy Settings
     */
    updateGlobalEconomy: async (settings) => {
        try {
            const docRef = doc(db, "settings", "global_economy");
            await setDoc(docRef, {
                ...settings,
                updatedAt: serverTimestamp()
            }, { merge: true });
            return true;
        } catch (error) {
            console.error("AdminEconomyService.updateGlobalEconomy Error:", error);
            throw error;
        }
    },

    /**
     * [SWIS] Get All Star Products
     */
    getProducts: async () => {
        try {
            const q = query(collection(db, "star_products"), orderBy("price", "asc"));
            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        } catch (error) {
            console.error("AdminEconomyService.getProducts Error:", error);
            throw error;
        }
    },

    /**
     * [SWIS] Save Product (Add/Update)
     * Includes Hard Validation for 100k Limit
     */
    saveProduct: async (product) => {
        // Double Check 100k Limit
        if (product.price > 100000) {
            throw new Error("가격은 100,000원을 초과할 수 없습니다.");
        }

        try {
            const id = product.id || doc(collection(db, "star_products")).id;
            const docRef = doc(db, "star_products", id);

            await setDoc(docRef, {
                ...product,
                updatedAt: serverTimestamp()
            }, { merge: true });

            return true;
        } catch (error) {
            console.error("AdminEconomyService.saveProduct Error:", error);
            throw error;
        }
    },

    /**
     * [SWIS] Delete Product
     */
    deleteProduct: async (productId) => {
        try {
            await deleteDoc(doc(db, "star_products", productId));
            return true;
        } catch (error) {
            console.error("AdminEconomyService.deleteProduct Error:", error);
            throw error;
        }
    },

    // ============================================================
    // [DYNAMIC] Activity Rewards Config
    // ============================================================
    getActivityRewards: async () => {
        const DEFAULTS = {
            attendance: { amount: 10, enabled: true },
            reading: { amount: 1, enabled: true, dedup: true, dailyCap: 10 },
            welcome: { amount: 100, enabled: true },
            referral: { amount: 500, enabled: true },
            share: { amount: 5, enabled: true }
        };
        try {
            const snap = await getDoc(doc(db, "settings", "activity_rewards"));
            return snap.exists() ? { ...DEFAULTS, ...snap.data() } : { ...DEFAULTS };
        } catch (error) {
            console.error("AdminEconomyService.getActivityRewards Error:", error);
            return { ...DEFAULTS };
        }
    },

    updateActivityRewards: async (data) => {
        try {
            await setDoc(doc(db, "settings", "activity_rewards"), {
                ...data,
                updatedAt: serverTimestamp()
            }, { merge: true });
            return true;
        } catch (error) {
            console.error("AdminEconomyService.updateActivityRewards Error:", error);
            throw error;
        }
    }
};
